import { useState, useEffect } from 'react';
import { FiArrowLeft, FiPackage, FiUsers, FiRefreshCw, FiAlertCircle, FiCalendar } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Card } from '../../components/ui/Card/Card';
import { Badge } from '../../components/ui/Badge/Badge';
import { Button } from '../../components/ui/Button/Button';
import { useBilling } from '../../hooks/useBilling';
import { useUsageHistory } from '../../hooks/useUsageHistory';
import { usePlanUpgrade } from '../../hooks/usePlanUpgrade';
import { UsageProgress } from '../../components/Billing/UsageProgress/UsageProgress';
import { UsageHistory } from '../../components/Billing/UsageHistory/UsageHistory';
import { LowCreditsWarning } from '../../components/Billing/LowCreditsWarning/LowCreditsWarning';
import { CreditLimitExceededModal } from '../../components/Billing/CreditLimitExceededModal/CreditLimitExceededModal';
import { CancelSubscriptionModal } from '../../components/Billing/CancelSubscriptionModal/CancelSubscriptionModal';
import { PendingPlanChange } from '../../components/Billing/PendingPlanChange/PendingPlanChange';
import { BillingDetailsForm } from '../../components/Billing/BillingDetailsForm/BillingDetailsForm';
import BillingAccessStatus from '../../components/Billing/BillingAccessStatus/BillingAccessStatus';
import styles from './BillingUsage.module.css';

import { logger } from '../../utils/logger';

export default function BillingUsage() {
  const navigate = useNavigate();
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const [showLimitExceededModal, setShowLimitExceededModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [dismissedWarning, setDismissedWarning] = useState(false);

  // Fetch billing data
  const {
    billing,
    loading: billingLoading,
    error: billingError,
    refetch: refetchBilling,
    isLowCredits,
  } = useBilling(currentOrganization?.id);

  // Fetch usage history
  const {
    events,
    loading: historyLoading,
    error: historyError,
    setFilters,
  } = useUsageHistory({
    organizationId: currentOrganization?.id,
  });

  // Plan cancellation
  const { cancelSubscription, cancelling } = usePlanUpgrade();

  const handleCancelSubscription = async (cancelAtPeriodEnd: boolean, reason?: string) => {
    if (!currentOrganization?.id || cancelling) return;
    const result = await cancelSubscription(currentOrganization.id, cancelAtPeriodEnd, reason);
    if (result) {
      setShowCancelModal(false);
      await refetchBilling();
    }
  };

  // Check if user can cancel (paid plan, active, not already cancelling)
  const canCancel = billing
    && billing.plan.type !== 'free'
    && billing.subscription?.billing_source !== 'internal_testing'
    && billing.subscription?.status === 'active'
    && !billing.subscription?.cancel_at_period_end;

  const canManageBillingDetails = currentOrganization?.user_role === 'owner'
    || currentOrganization?.user_role === 'admin';

  const getPlanBadgeVariant = (plan: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'danger' | 'tertiary' => {
    switch (plan) {
      case 'enterprise': return 'primary';
      case 'team': return 'info';
      case 'pro': return 'accent' as any; // Using accent color
      case 'free': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatPlanName = (plan: string) => {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const formatBillingDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const handleRefresh = async () => {
    await refetchBilling();
  };

  const handleUpgradeClick = () => {
    navigate('/admin/billing/upgrade');
  };

  // Check if credits are exceeded and show modal
  useEffect(() => {
    if (billing && billing.plan.limits.credits.used > billing.plan.limits.credits.limit) {
      setShowLimitExceededModal(true);
    }
  }, [billing]);

  // Determine warning variant
  const getWarningVariant = () => {
    if (!billing) return 'warning';
    const percentage = (billing.plan.limits.credits.remaining / billing.plan.limits.credits.limit) * 100;
    return percentage < 10 ? 'critical' : 'warning';
  };

  // Loading state - wait for both organization and billing data
  if (orgLoading || billingLoading || !currentOrganization) {
    return (
      <div className={styles.billingPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <Link to="/admin" className={styles.backButton}>
              <FiArrowLeft />
              Back to Admin
            </Link>
            <h1>Billing & Usage</h1>
            <p>Loading billing information...</p>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Fetching your billing data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (billingError) {
    return (
      <div className={styles.billingPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <Link to="/admin" className={styles.backButton}>
              <FiArrowLeft />
              Back to Admin
            </Link>
            <h1>Billing & Usage</h1>
            <p>Error loading billing information</p>
          </div>
        </div>
        <Card className={styles.errorCard}>
          <FiAlertCircle className={styles.errorIcon} />
          <h3>Failed to Load Billing Data</h3>
          <p>{billingError}</p>
          <Button variant="primary" onClick={handleRefresh}>
            <FiRefreshCw />
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  // Debug logging to see what backend is returning
  if (billing) {
    logger.log('🔍 BillingUsage Debug:', {
      plan_type: billing.plan.type,
      plan_name: billing.plan.name,
      has_pending_change: billing.next_billing?.has_pending_change,
      next_billing: billing.next_billing,
    });
  }

  return (
    <div className={styles.billingPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <Link to="/admin" className={styles.backButton}>
            <FiArrowLeft />
            Back to Admin
          </Link>
          <h1>Billing & Usage</h1>
          <p>Manage your organization's subscription and usage</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>
            <FiRefreshCw />
            Refresh
          </Button>
          {billing && billing.plan.type !== 'enterprise' && (
            <Button variant="primary" size="md" onClick={handleUpgradeClick}>
              Upgrade Plan
            </Button>
          )}
        </div>
      </div>

      {/* Low Credits Warning */}
      {isLowCredits && billing && !dismissedWarning && (
        <LowCreditsWarning
          creditsRemaining={billing.plan.limits.credits.remaining}
          creditsLimit={billing.plan.limits.credits.limit}
          onUpgrade={handleUpgradeClick}
          onDismiss={() => setDismissedWarning(true)}
          variant={getWarningVariant()}
        />
      )}

      {/* Current Plan & Usage */}
      <div className={styles.planSection}>
        <Card className={styles.planCard}>
          <div className={styles.planHeader}>
            <div className={styles.planInfo}>
              <FiPackage className={styles.planIcon} />
              <div>
                <h2>Current Plan</h2>
                <p className={styles.planDescription}>
                  Your organization is on the{' '}
                  <Badge variant={getPlanBadgeVariant(billing?.plan.type || 'free')}>
                    {formatPlanName(billing?.plan.name || 'Free')}
                  </Badge>{' '}
                  plan
                </p>
              </div>
            </div>
            {billing?.billing_period?.end && (
              <div className={styles.billingCycleInfo}>
                <div className={styles.billingCycleRow}>
                  <FiCalendar className={styles.calendarIcon} />
                  <div className={styles.billingCycleText}>
                    <span className={styles.billingCycleLabel}>
                      {billing.subscription.billing_source === 'internal_testing' ? 'Credits reset' : billing.subscription.cancel_at_period_end ? 'Ends' : 'Renews'}
                    </span>
                    <span className={styles.billingCycleDate}>
                      {formatBillingDate(billing.billing_period.end)}
                    </span>
                  </div>
                </div>
                {billing.subscription.status && (
                  <BillingAccessStatus status={billing.subscription.status} entitlement={billing.entitlement} />
                )}
              </div>
            )}
          </div>

          {billing && (
            <>
              {/* Pending Plan Change Notice */}
              {billing.next_billing?.has_pending_change && (
                <PendingPlanChange
                  nextBilling={billing.next_billing}
                  currentPlanName={billing.plan.name}
                />
              )}

              {billing.subscription?.cancel_at_period_end && (
                <div className={styles.cancellationNotice}>
                  <FiAlertCircle />
                  <span>Your subscription will be canceled at the end of the current billing period</span>
                </div>
              )}

              {/* Credit Usage */}
              <div className={styles.usageSection}>
                <h3>Credit Usage</h3>
                <UsageProgress
                  current={billing.plan.limits.credits.used}
                  limit={billing.plan.limits.credits.limit}
                  label="Credits Used This Month"
                  showPercentage
                  showWarning
                />
              </div>

              {/* Next Billing Info */}
              {billing.next_billing && (
                <div className={styles.nextBillingSection}>
                  <h3>{billing.subscription.billing_source === 'internal_testing' ? 'Next credit reset' : 'Next Billing'}</h3>
                  <div className={styles.nextBillingCard}>
                    <div className={styles.nextBillingRow}>
                      <FiCalendar className={styles.nextBillingIcon} />
                      <div>
                        <span className={styles.nextBillingLabel}>{billing.subscription.billing_source === 'internal_testing' ? 'Reset date:' : 'Next Billing Date:'}</span>
                        <span className={styles.nextBillingValue}>
                          {formatBillingDate(billing.next_billing.next_billing_date)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.nextBillingRow}>
                      <FiPackage className={styles.nextBillingIcon} />
                      <div>
                        <span className={styles.nextBillingLabel}>{billing.subscription.billing_source === 'internal_testing' ? 'Monthly test allowance:' : 'Credits after successful renewal:'}</span>
                        <span className={styles.nextBillingValue}>
                          {billing.next_billing.credits_to_be_granted.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className={styles.nextBillingRow}>
                      <span className={styles.nextBillingLabel}>Plan:</span>
                      <span className={styles.nextBillingValue}>{billing.next_billing.plan_name}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Limits */}
              <div className={styles.limitsGrid}>
                <div className={styles.limitCard}>
                  <FiUsers className={styles.limitIcon} />
                  <div className={styles.limitContent}>
                    <span className={styles.limitLabel}>Team Members</span>
                    <span className={styles.limitValue}>
                      {billing.plan.limits.members.current} / {billing.plan.limits.members.max || '∞'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cancel Subscription */}
              {canCancel && (
                <div className={styles.cancelSection}>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setShowCancelModal(true)}
                    className={styles.cancelButton}
                  >
                    Cancel Subscription
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Invoice Billing Details */}
      <BillingDetailsForm
        organizationId={currentOrganization.id}
        organizationName={currentOrganization.name}
        canEdit={canManageBillingDetails}
      />

      {/* Usage History */}
      <Card className={styles.historyCard}>
        <div className={styles.historyHeader}>
          <h2>Usage History</h2>
          <p>Recent credit usage activity</p>
        </div>
        <div className={styles.historyContent}>
          {historyError ? (
            <div className={styles.errorState}>
              <FiAlertCircle className={styles.errorIcon} />
              <p>Failed to load usage history</p>
              <span className={styles.errorNote}>{historyError}</span>
            </div>
          ) : (
            <UsageHistory
              events={events}
              loading={historyLoading}
              onFilterChange={(service) => setFilters({ service })}
            />
          )}
        </div>
      </Card>

      {/* Organization Info */}
      <Card className={styles.orgInfoCard}>
        <h3>Organization Details</h3>
        <div className={styles.orgDetails}>
          <div className={styles.orgDetailItem}>
            <span className={styles.orgDetailLabel}>Organization Name:</span>
            <span className={styles.orgDetailValue}>{currentOrganization?.name}</span>
          </div>
          <div className={styles.orgDetailItem}>
            <span className={styles.orgDetailLabel}>Organization ID:</span>
            <span className={styles.orgDetailValue}>{currentOrganization?.id}</span>
          </div>
          <div className={styles.orgDetailItem}>
            <span className={styles.orgDetailLabel}>Organization Billing Email:</span>
            <span className={styles.orgDetailValue}>{currentOrganization?.billing_email || 'Not set'}</span>
          </div>
          <div className={styles.orgDetailItem}>
            <span className={styles.orgDetailLabel}>Status:</span>
            <Badge variant={currentOrganization?.is_active ? 'success' : 'warning'}>
              {currentOrganization?.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Credit Limit Exceeded Modal */}
      {billing && (
        <CreditLimitExceededModal
          isOpen={showLimitExceededModal}
          onClose={() => setShowLimitExceededModal(false)}
          onUpgrade={() => {
            setShowLimitExceededModal(false);
            navigate('/admin/billing/upgrade');
          }}
          creditsUsed={billing.plan.limits.credits.used}
          creditsLimit={billing.plan.limits.credits.limit}
          currentPlan={billing.plan.name}
        />
      )}

      {/* Cancel Subscription Modal */}
      {billing && (
        <CancelSubscriptionModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelSubscription}
          loading={cancelling}
          currentPlan={billing.plan.name}
          periodEnd={billing.billing_period?.end}
        />
      )}
    </div>
  );
}

